import { GatsbyImage } from 'gatsby-plugin-image';
import Cookies from 'js-cookie';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Client, HydrationProvider } from 'react-hydration-provider';

import { setUnlockedCookie, useCheckGatedCookie } from '../../../tools/hooks/useCheckGatedCookie';
import { ArrowLink } from '../../Common/Links';
import EmbeddedMarketoForm from '../../Common/MarketoForm/EmbeddedMarketoForm';
import RichTextConverter from '../../Common/RichText/RichTextConverter';
import { smoothScrollToRef } from '../../Common/ScrollTo';
import * as styles from './GatedForm.module.scss';

const COOKIE = '_fs_axs';

const GatedForm = ({
  title,
  successCopy,
  fileUrl,
  contentIsDownloadable,
  heroImage,
  marketoFormId,
  onSuccess,
  trackingPixelUrl,
  uniqueTrackingPixel,
  downloadCtaLabel,
  slug,
  className,
}) => {
  const cookieName = COOKIE;
  const [showFormSuccess, setShowFormSuccess] = useState(!!Cookies.get(cookieName));
  const [successMessage, setSuccessMessage] = useState(
    <div className={styles.success} data-analytics-section={'form-success-message'}>
      <h2>Thank You</h2>
      <p>Please check your email for the file.</p>
    </div>
  );

  const formRef = useRef(null);

  let label = downloadCtaLabel || 'Download the paper';

  const onFormSuccess = (formValues, formId) => {
    setShowFormSuccess(true);
    setUnlockedCookie(formId);
    smoothScrollToRef(formRef, 'smooth');
    onSuccess && onSuccess();
  };

  useCheckGatedCookie(setShowFormSuccess, showFormSuccess);

  const isDownloadable = contentIsDownloadable?.[0] === 'Yes';
  const hasOverride = !!successCopy;

  useEffect(() => {
    if (isDownloadable && !hasOverride) {
      setSuccessMessage(
        <div
          key="arrow-content-available"
          className={[styles.success, 'gated-download-content-available'].join(' ')}
          data-analytics-section={'form-success-message'}
        >
          <div id="recaptcha-container" className="g-recaptcha" data-size="invisible"></div>
          {fileUrl && <ArrowLink url={fileUrl} label={label} isArrowDown isLarge isExternal isDownload />}
        </div>
      );
    } else if (isDownloadable && hasOverride) {
      setSuccessMessage(
        <div
          key="rich-text-arrow-content-available"
          className={[styles.success, 'gated-download-content-available'].join(' ')}
          data-analytics-section={'form-success-message'}
        >
          <div id="recaptcha-container" className="g-recaptcha" data-size="invisible"></div>
          <RichTextConverter copy={successCopy} />
          {fileUrl && (
            <div>
              <ArrowLink url={fileUrl} label={label} isArrowDown isLarge isExternal isDownload />
            </div>
          )}
        </div>
      );
    } else if (hasOverride) {
      setSuccessMessage(
        <div
          key="rich-text-content-available"
          className={[styles.success, 'gated-content-available'].join(' ')}
          data-analytics-section={'form-success-message'}
        >
          <div id="recaptcha-container" className="g-recaptcha" data-size="invisible"></div>
          <RichTextConverter copy={successCopy} />
        </div>
      );
    }
  }, [isDownloadable, hasOverride]);

  const imageData = get(heroImage, 'gatsbyImageData');
  const img = get(heroImage, 'file.url');
  let image;
  if (imageData) {
    image = (
      <GatsbyImage
        image={get(heroImage, 'gatsbyImageData')}
        className={styles.heroImage}
        imgStyle={{ objectFit: 'contain' }}
        alt={title}
      />
    );
  } else if (img) {
    image = <img src={img} className={styles.heroImage} loading="lazy" alt={title} />;
  }

  let form = (
    <EmbeddedMarketoForm
      onSuccess={(vals) => {
        onFormSuccess(vals, marketoFormId);
      }}
      className={className}
      slug={slug}
      formId={marketoFormId}
    />
  );

  return (
    <HydrationProvider>
      <Client>
        <div className={styles.container} ref={formRef} data-analytics-component={'form-wrapper'}>
          {image && <div className={styles.imgContainer}>{image}</div>}
          {!showFormSuccess && form}
          {showFormSuccess && (
            <div key="success-message">
              {successMessage}
              {trackingPixelUrl && (
                <img src={trackingPixelUrl} height="1" width="1" style={{ display: 'none' }} alt="" />
              )}
            </div>
          )}
          {showFormSuccess && uniqueTrackingPixel && (
            <img src={uniqueTrackingPixel} height="1" width="1" style={{ display: 'none' }} alt="" />
          )}
        </div>
      </Client>
    </HydrationProvider>
  );
};

export const propTypes = {
  title: PropTypes.string,
  successCopy: PropTypes.object,
  fileUrl: PropTypes.string,
  contentIsDownloadable: PropTypes.array,
  heroImage: PropTypes.object,
  marketoFormId: PropTypes.string,
};

GatedForm.propTypes = {
  ...propTypes,
};

export default GatedForm;
